import React, { useEffect, useState } from "react";
import EventForm from "@/components/events/EventForm";
import EventLimitsBanner from "@/components/subscription/EventLimitsBanner";
import { useAuth } from "@/app/hooks/useAuth";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import CustomCalendarDialog from "@/components/calendar/CustomCalendarDialog";

interface CustomCalendar {
  id: string;
  name: string;
  color: string;
}

const CreateEvent: React.FC = () => {
  const { user } = useAuth();
  const isPro = user?.plan === 'pro';
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState("");
  const [groupId, setGroupId] = useState<string | null>(null);
  const [customCalendars, setCustomCalendars] = useState<CustomCalendar[]>([]);
  const [initialDate, setInitialDate] = useState<string | null>(null);
  
  console.log("CreateEvent component - Location:", location);
  console.log("CreateEvent component - Params:", params);
  
  // Get initial date from location state if available
  useEffect(() => {
    if (location.state && location.state.initialDate) {
      console.log("Initial date from location state:", location.state.initialDate);
      setInitialDate(location.state.initialDate);
    }
  }, [location.state]);
  
  // Fetch user's custom calendars if they are a PRO user
  useEffect(() => {
    const fetchCustomCalendars = async () => {
      if (user && isPro) {
        try {
          const { data, error } = await supabase
            .from('ft_custom_calendars')
            .select('id, name, color')
            .eq('user_id', user.id);
            
          if (error) {
            console.error("Error fetching custom calendars:", error);
            return;
          }
          
          if (data) {
            console.log("Custom calendars fetched:", data);
            setCustomCalendars(data);
          }
        } catch (err) {
          console.error("Error fetching custom calendars:", err);
        }
      }
    };
    
    fetchCustomCalendars();
  }, [user, isPro]);
  
  useEffect(() => {
    // Get group from URL for title display and form initialization
    const getGroupInfo = async () => {
      // First check direct params if available
      if (params && params.group) {
        console.log("Group param found:", params.group);
        try {
          // Try to fetch the actual group name from database if user is authenticated
          if (user) {
            const { data, error } = await supabase
              .from('ft_groups')
              .select('name, id')
              .eq('id', params.group)
              .single();
              
            if (!error && data) {
              console.log("Group data from DB:", data);
              setGroupName(data.name);
              setGroupId(data.id);
              return;
            }
          }
          
          // Fallback to capitalizing the URL parameter
          const group = params.group.charAt(0).toUpperCase() + params.group.slice(1);
          setGroupName(group);
          setGroupId(params.group.toLowerCase());
        } catch (error) {
          console.error("Error fetching group info:", error);
          // Fallback
          const group = params.group.charAt(0).toUpperCase() + params.group.slice(1);
          setGroupName(group);
          setGroupId(params.group.toLowerCase());
        }
        return;
      }
      
      // Otherwise, check path
      const path = location.pathname;
      console.log("Checking path for group:", path);
      
      if (path.includes('family')) {
        // Try to fetch the actual family group from database
        if (user) {
          try {
            const { data, error } = await supabase
              .from('ft_groups')
              .select('id')
              .eq('created_by', user.id)
              .eq('name', 'Family')
              .single();
              
            if (!error && data) {
              console.log("Found family group:", data);
              setGroupName('Family');
              setGroupId(data.id);
              return;
            }
          } catch (error) {
            console.error("Error fetching family group:", error);
          }
        }
        setGroupName('Family');
        setGroupId('family');
      }
      else if (path.includes('friends')) {
        // Try to fetch the actual friends group from database
        if (user) {
          try {
            const { data, error } = await supabase
              .from('ft_groups')
              .select('id')
              .eq('created_by', user.id)
              .eq('name', 'Friends')
              .single();
              
            if (!error && data) {
              console.log("Found friends group:", data);
              setGroupName('Friends');
              setGroupId(data.id);
              return;
            }
          } catch (error) {
            console.error("Error fetching friends group:", error);
          }
        }
        setGroupName('Friends');
        setGroupId('friends');
      }
      else if (path.includes('hobby')) {
        setGroupName('Hobby');
        setGroupId('hobby');
      }
      else if (path.includes('holidays')) {
        setGroupName('Holidays');
        setGroupId('holidays');
      }
      else {
        setGroupName('New');
        setGroupId(null);
      }
    };
    
    getGroupInfo();
  }, [params, location.pathname, user]);
  
  const refreshCustomCalendars = async () => {
    if (user && isPro) {
      try {
        const { data, error } = await supabase
          .from('ft_custom_calendars')
          .select('id, name, color')
          .eq('user_id', user.id);
          
        if (!error && data) {
          console.log("Refreshed custom calendars:", data);
          setCustomCalendars(data);
        }
      } catch (err) {
        console.error("Error refreshing custom calendars:", err);
      }
    }
  };
  
  console.log("Rendering CreateEvent with groupId:", groupId, "groupName:", groupName, "initialDate:", initialDate);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Button 
            variant="ghost"
            size="icon"
            className="mr-3"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-black">Create {groupName} Event</h1>
        </div>
        
        {isPro && (
          <CustomCalendarDialog onCalendarCreated={refreshCustomCalendars} />
        )}
      </div>
      
      {/* Only show the event limits banner for free users */}
      {!isPro && <EventLimitsBanner subscriptionType={user?.plan} />}
      
      {/* Pass group information, custom calendars, and initial date to the event form */}
      <EventForm 
        initialGroup={groupId} 
        customCalendars={customCalendars} 
        initialDate={initialDate}
      />
    </div>
  );
};

export default CreateEvent;
